<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import {
  prefetchCategoryProductsFastQuery,
  prefetchNewProductsQuery,
  useCategoryProductsFastQuery,
  useHomeContentQuery,
  useHomeQuery,
  useNewProductsQuery,
} from "~~/app/composables/queries/useStoreApi";
import { homeContentDefaultForBrand } from "~~/shared/constants/homeContentDefault";
import type { HomeCategoryTab } from "~~/shared/types/store";

const brand = useBrand();
const { sellingEnabled } = useCatalogOnly();
const { data: home } = useHomeQuery();
const { data: homeContentData } = useHomeContentQuery();
// Treść zapasowa dobrana do MARKI — wspólny default był treścią lavmaga,
// więc sklep gadżetów pokazywał „MODA LESS WASTE" i wyprzedaż sukienek.
const content = computed(
  () => homeContentData.value?.homeContent ?? homeContentDefaultForBrand(brand.id),
);
const homepageTabs = computed<HomeCategoryTab[]>(
  () => content.value.categoryTabs,
);
/**
 * Hero ze znakiem firmowym zamiast fotografii — wariant sklepu katalogowego.
 *
 * `sellingEnabled` z useCatalogOnly() jest zwykłym booleanem, nie refem:
 * `.value` dałoby tu `undefined`, czyli wartość falsy, i lavmag dostałby
 * logo w hero, gdyby kiedykolwiek został bez zdjęcia.
 */
const pokazZnakWHero = computed(
  () => !content.value.hero.image && !sellingEnabled,
);
const recentlyViewed = useRecentlyViewedProducts();

const heroEl = ref<HTMLElement | null>(null);
const heroImgEl = ref<HTMLElement | null>(null);
const heroContentEl = ref<HTMLElement | null>(null);
const saleBannerEl = ref<HTMLElement | null>(null);
const saleBannerImgEl = ref<HTMLElement | null>(null);

const { cleanup: cleanupScrollAnimations } = useScrollAnimations({
  heroEl,
  heroImgEl,
  heroContentEl,
  saleBannerEl,
  saleBannerImgEl,
});

onUnmounted(cleanupScrollAnimations);

useHead({
  style: [
    {
      innerHTML: `@media not (prefers-reduced-motion: reduce) {
      .hero__img { animation: hero-img-reveal 1.2s ease-out both; }
      @keyframes hero-img-reveal {
        /* Uwaga: NIE animujemy opacity od 0 — hero to element LCP, a fade-in
           opóźnia moment, w którym Chrome uznaje go za wyrenderowany (LCP rośnie).
           Zostaje sam subtelny zoom (transform, kompozytowany, nie blokuje LCP
           ani nie powoduje CLS). */
        from { opacity: 1; transform: scale(1.06); }
        to { opacity: 1; transform: scale(1); }
      }
      .hero__content > * { animation: hero-reveal 0.7s ease-out both; animation-delay: 0.1s; }
      .hero__content > *:nth-child(2) { animation-delay: 0.25s; }
      .hero__content > *:nth-child(3) { animation-delay: 0.4s; }
      @keyframes hero-reveal {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: none; }
      }
      .product-editorial-section__title { opacity: 0; transform: translateY(30px); }
      .product-editorial-section__item:not(.product-editorial-section__skeleton-card) { opacity: 0; transform: translateY(30px); }
      .products-section:not(.products-section--first) .section-header { opacity: 0; transform: translateY(30px); }
    }`,
    },
  ],
});

// Tytuł strony głównej zawiera już nazwę brandu (seo.defaultTitle), więc na tej
// stronie nadpisujemy globalny titleTemplate na '%s', aby uniknąć podwójnego
// sufiksu brandu (np. „… · LAVMAG · LAVMAG").
useHead({ titleTemplate: "%s" });
useStorefrontSeo({
  title: brand.seo.defaultTitle,
  description: brand.seo.defaultDescription,
  image: content.value.hero.image,
});

const activeTab = ref<string>("");
watchEffect(() => {
  const tabs = homepageTabs.value;
  if (!tabs.some((t) => t.label === activeTab.value)) {
    const firstCategory = tabs.find((t) => t.type === "category") ?? tabs[0];
    activeTab.value = firstCategory?.label ?? "";
  }
});
const activeTabConfig = computed(
  () =>
    homepageTabs.value.find((tab) => tab.label === activeTab.value) ??
    homepageTabs.value[0],
);
const activeCategoryId = computed(() =>
  activeTabConfig.value?.type === "category"
    ? (activeTabConfig.value.id ?? "0")
    : "0",
);
const activeCategoryQuery = computed(() => ({
  currentUrl:
    activeTabConfig.value?.type === "category"
      ? (activeTabConfig.value.href ?? "")
      : "/",
  resultsPerPage: 12,
  order: "product.date_add.desc",
}));
const newestProductsQuery = computed(() => ({
  currentUrl: "/nowosci",
  resultsPerPage: 12,
  order: "product.date_add.desc",
}));

const { data: activeCategoryProducts, isSuccess: isCategorySuccess } =
  useCategoryProductsFastQuery(activeCategoryId, activeCategoryQuery, {
    enabled: computed(() => activeTabConfig.value?.type === "category"),
    keepPreviousData: false,
  });
const { data: tabNewestProducts, isSuccess: isNewestSuccess } =
  useNewProductsQuery(newestProductsQuery, {
    enabled: computed(() => activeTabConfig.value?.type === "new"),
    keepPreviousData: false,
  });
const { data: recentlyAdded } = useNewProductsQuery(newestProductsQuery);

const queryClient = useQueryClient();

const emptyTabs = ref(new Set<string>());

// Tab "Nowości" (type "new") ukryty z renderowania (Asana 2026-06-16) — na mobile
// nie wyświetlał produktów; domyślną sekcją jest "Kobieta". Pełna lista nowości
// pozostaje pod /nowosci. Aby przywrócić: usuń warunek tab.type !== "new".
const visibleTabs = computed(() =>
  homepageTabs.value.filter((tab) => !emptyTabs.value.has(tab.label)),
);

function markTabEmpty(label: string) {
  if (emptyTabs.value.has(label)) return;
  const next = new Set(emptyTabs.value);
  next.add(label);
  emptyTabs.value = next;
  if (activeTab.value === label) {
    const fallback = visibleTabs.value[0];
    if (fallback) activeTab.value = fallback.label;
  }
}

watch(
  [
    activeTab,
    tabNewestProducts,
    activeCategoryProducts,
    isNewestSuccess,
    isCategorySuccess,
  ],
  () => {
    const config = activeTabConfig.value;
    if (config?.type === "new" && isNewestSuccess.value) {
      if ((tabNewestProducts.value?.products?.length ?? 0) === 0) {
        markTabEmpty(activeTab.value);
      }
    } else if (config?.type === "category" && isCategorySuccess.value) {
      if ((activeCategoryProducts.value?.products?.length ?? 0) === 0) {
        markTabEmpty(activeTab.value);
      }
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (!import.meta.client) return;
  prefetchHomepageTabs();
});

function prefetchHomepageTabs() {
  const schedule = (cb: () => void) => {
    const idle = (
      window as unknown as {
        requestIdleCallback?: (
          cb: () => void,
          opts?: { timeout?: number },
        ) => number;
      }
    ).requestIdleCallback;
    if (typeof idle === "function") {
      idle(cb, { timeout: 2000 });
    } else {
      setTimeout(cb, 150);
    }
  };

  schedule(async () => {
    for (const tab of homepageTabs.value) {
      if (tab.label === activeTab.value) continue;

      let count = 0;
      if (tab.type === "new") {
        const data = await prefetchNewProductsQuery(
          queryClient,
          newestProductsQuery.value,
        );
        count = data?.products?.length ?? 0;
      } else if (tab.type === "category") {
        const data = await prefetchCategoryProductsFastQuery(
          queryClient,
          tab.id ?? "0",
          {
            currentUrl: tab.href ?? "",
            resultsPerPage: 12,
            order: "product.date_add.desc",
          },
        );
        count = data?.products?.length ?? 0;
      }

      if (count === 0) markTabEmpty(tab.label);
    }
  });
}

const tabProducts = computed(() =>
  activeTabConfig.value?.type === "new"
    ? (tabNewestProducts.value?.products ?? [])
    : (activeCategoryProducts.value?.products ?? []),
);
const recentlyViewedProducts = computed(() =>
  recentlyViewed.products.value.slice(0, 12),
);
/**
 * „Ostatnio dodane" i zakładka „Nowości" czytają to samo zapytanie
 * (`newestProductsQuery`). Gdy poza nowościami nie ma innych zakładek, obie
 * sekcje pokazują identyczne produkty — proles widział dwa razy tę samą
 * siódemkę. Zostaje karuzela „Nowości" (nazwa jak w menu i na /nowosci);
 * gdy panel doda zakładkę działu, karuzela startuje od działu i „Ostatnio
 * dodane" wraca. Tylko dla marek z `homeSkipDuplicateNewProducts`.
 */
const ostatnioDodaneDubluje = computed(
  () =>
    brand.homeSkipDuplicateNewProducts === true &&
    visibleTabs.value.length > 0 &&
    visibleTabs.value.every((tab) => tab.type === "new"),
);
const recentlyAddedProducts = computed(() =>
  ostatnioDodaneDubluje.value ? [] : (recentlyAdded.value?.products ?? []),
);
const bestsellersProducts = computed(() => {
  const trending = home.value?.trending ?? [];
  return trending.slice(0, 8);
});

/**
 * Sekcja pod hero: marka z `homeFeaturedCategory` (lavmag — „Sukienki")
 * pokazuje tu nowo dodane produkty swojej kategorii zamiast bestsellerów.
 * Pusta kategoria albo błąd zapytania = wracają bestsellery, żeby pod hero
 * nie zostawała dziura. Pozostałe marki nie wysyłają tego zapytania.
 *
 * W trakcie ładowania sekcji nie ma (bez szkieletu), tak jak bestsellery:
 * useScrollAnimations odsłania kafle jednorazowo przy pierwszym pojawieniu
 * się sekcji — szkielet „zużyłby" tę animację i dociągnięte produkty
 * zostałyby z opacity: 0.
 */
const featuredCategory = brand.homeFeaturedCategory;
const {
  data: featuredCategoryData,
  isSuccess: isFeaturedSuccess,
  isError: isFeaturedError,
} = useCategoryProductsFastQuery(
  () => featuredCategory?.id ?? "0",
  () => ({
    currentUrl: featuredCategory?.href ?? "",
    resultsPerPage: 8,
    order: "product.date_add.desc",
  }),
  { enabled: Boolean(featuredCategory), keepPreviousData: false },
);
const editorialSection = computed(() => {
  const bestsellers = {
    title: "Bestsellery",
    products: bestsellersProducts.value,
    itemListId: "home_bestsellers",
    itemListName: "Bestsellery",
  };
  if (!featuredCategory) return bestsellers;

  const products = featuredCategoryData.value?.products ?? [];
  const settled = isFeaturedSuccess.value || isFeaturedError.value;
  if (settled && products.length === 0) return bestsellers;

  return {
    title: featuredCategory.title,
    products: products.slice(0, 8),
    itemListId: `home_category_${featuredCategory.id}`,
    itemListName: featuredCategory.title,
  };
});

const shippingMessages = computed(() => [
  `Darmowa dostawa od ${brand.freeShippingThreshold} zł`,
]);
</script>

<template>
  <div class="home">
    <!-- ── Hero ── -->
    <section
      ref="heroEl"
      class="hero"
      :class="{
        'hero--no-media': !content.hero.image,
        'hero--marka': pokazZnakWHero,
      }"
    >
      <!-- Bez zdjęcia hero zostaje blokiem tekstowym na tle motywu marki.
           Nowy sklep dostaje materiały graficzne później (E6.4), a puste
           <NuxtImg> renderowało pusty prostokąt zamiast treści. -->
      <div v-if="content.hero.image" ref="heroImgEl" class="hero__media">
        <NuxtImg
          :src="content.hero.image"
          :alt="content.hero.title"
          width="1920"
          height="765"
          sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw 3xl:100vw"
          format="webp"
          class="hero__img"
          fetchpriority="high"
          loading="eager"
        />
        <div class="hero__overlay" />
      </div>
      <div ref="heroContentEl" class="hero__content">
        <!-- Znak firmowy zamiast fotografii: klientka poprosiła, żeby na
             tytułowej było „samo LOGO proles i hasło". Tylko w wariancie bez
             zdjęcia — na fotografii logo i tak stoi w nagłówku strony, a tu
             konkurowałoby z kadrem. Alt z nazwą marki: tytułem jest hasło
             oferty („GADŻETY REKLAMOWE Z LOGO"), więc nazwy nie niesie nic
             innego w hero — a audyt zgłaszał ten obrazek jako nieopisany. -->
        <img
          v-if="pokazZnakWHero"
          :src="brand.logo.src"
          :alt="brand.name"
          class="hero__logo"
          width="320"
          height="99"
        >
        <!-- Tagline marki nad tytułem. Pole `eyebrow` było w treści od
             początku, ale nigdy się nie renderowało — sklepy sprzedażowe
             zostawiają je puste, więc `v-if` nic im nie zmienia. -->
        <p v-if="content.hero.eyebrow" class="hero__eyebrow">
          {{ content.hero.eyebrow }}
        </p>
        <h1 class="hero__title">{{ content.hero.title }}</h1>
        <p class="hero__subtitle">{{ content.hero.text }}</p>
        <NuxtLink
          :to="content.hero.ctaHref"
          class="btn btn-pill-light hero__cta"
        >
          {{ content.hero.ctaLabel }}
        </NuxtLink>
      </div>
    </section>

    <!-- ── Kafle kategorii (tylko katalog) ──
         W trybie katalogowym zajmują miejsce sekcji „Znajdź swój styl", która
         jest z języka mody. Kategorie brane z realnego drzewa nawigacji. -->
    <CatalogCategoryTiles
      v-if="!sellingEnabled"
      title="Czego szukasz?"
    />

    <!-- ── Shipping strip ──
         Tylko dla sklepów, które sprzedają: w trybie katalogowym nie ma
         koszyka ani dostawy, więc „Darmowa dostawa od 0 zł" obiecywałaby
         usługę, której sklep nie świadczy. -->
    <div v-if="sellingEnabled" class="shipping-strip" aria-hidden="true">
      <div class="shipping-strip__track">
        <template v-for="copy in 2" :key="copy">
          <div
            class="shipping-strip__inner"
            :aria-hidden="copy === 2 ? 'true' : undefined"
          >
            <template
              v-for="(msg, i) in Array(8).fill(shippingMessages[0])"
              :key="i"
            >
              <span>Darmowa dostawa od <strong>{{ brand.freeShippingThreshold }} zł</strong></span>
              <span class="shipping-strip__dot" />
            </template>
          </div>
        </template>
      </div>
    </div>

    <!-- ── Bestsellery albo kategoria marki (editorial) ── -->
    <ProductEditorialSection
      :title="editorialSection.title"
      :products="editorialSection.products"
      :item-list-id="editorialSection.itemListId"
      :item-list-name="editorialSection.itemListName"
    />

    <!-- ── Sale banner (nakładka tekst + przycisk na czystym obrazku) ──
         Wyprzedaż w sklepie, który nie sprzedaje, jest bez sensu — a przy
         wspólnej treści zapasowej sklep gadżetów wyświetlał tu baner
         „SUKIENKI — letnia wyprzedaż do -70%". -->
    <section v-if="sellingEnabled" ref="saleBannerEl" class="sale-banner">
      <NuxtLink
        :to="content.saleBanner.ctaHref"
        class="sale-banner__link"
        :aria-label="content.saleBanner.ctaLabel || content.saleBanner.title"
      >
        <div ref="saleBannerImgEl" class="sale-banner__media">
          <!-- desktop/mobile rozdzielone CSS-em (display:none). `display:none` NIE
               blokuje pobrania <img>, więc bez loading="lazy" WERSJA DESKTOP (~282 KB)
               ładowała się także na mobile. loading="lazy" + brak boxa (display:none)
               = przeglądarka nie pobiera ukrytego wariantu. Baner jest i tak pod
               bestsellerami (poza pierwszym ekranem), więc lazy jest tu bezpieczne. -->
          <NuxtImg
            :src="content.saleBanner.image"
            :alt="content.saleBanner.title"
            width="1921"
            height="760"
            format="webp"
            loading="lazy"
            class="sale-banner__img sale-banner__img--desktop"
          />
          <NuxtImg
            v-if="content.saleBanner.imageMobile"
            :src="content.saleBanner.imageMobile"
            :alt="content.saleBanner.title"
            width="403"
            height="325"
            format="webp"
            loading="lazy"
            class="sale-banner__img sale-banner__img--mobile"
          />
        </div>
        <div class="sale-banner__content">
          <h2 v-if="content.saleBanner.title" class="sale-banner__title">
            {{ content.saleBanner.title }}
          </h2>
          <p v-if="content.saleBanner.text" class="sale-banner__subtitle">
            {{ content.saleBanner.text }}
          </p>
          <span
            v-if="content.saleBanner.ctaLabel"
            class="btn btn-pill-light sale-banner__cta"
          >
            {{ content.saleBanner.ctaLabel }}
          </span>
        </div>
      </NuxtLink>
    </section>

    <section v-if="visibleTabs.length" class="products-section">
      <div class="section-header container">
        <div class="tabs">
          <button
            v-for="tab in visibleTabs"
            :key="tab.label"
            class="tabs__btn"
            :class="{ 'tabs__btn--active': activeTab === tab.label }"
            type="button"
            @click="activeTab = tab.label"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>
      <ProductScrollSnapCarousel
        v-if="tabProducts.length"
        :products="tabProducts"
        :item-list-id="`home_tab_${activeTab}`"
        :item-list-name="`Strona główna · ${activeTab}`"
      />
    </section>

    <!-- ── Recently viewed (carousel) ── -->
    <ClientOnly>
      <section
        v-if="recentlyViewed.isLoaded.value && recentlyViewedProducts.length"
        class="products-section products-section--first"
      >
        <div class="section-header container">
          <h2 class="section-title">Ostatnio oglądane</h2>
        </div>
        <ProductScrollSnapCarousel
          :products="recentlyViewedProducts"
          item-list-id="recently_viewed"
          item-list-name="Ostatnio oglądane"
        />
      </section>
    </ClientOnly>

    <!-- ── Recently added (4×3 grid) ── -->
    <section
      v-if="recentlyAddedProducts.length"
      class="products-section container"
    >
      <h2 class="section-title">Ostatnio dodane</h2>
      <div class="recently-added-grid">
        <ProductCard
          v-for="(product, index) in recentlyAddedProducts.slice(0, 12)"
          :key="product.id"
          :product="product"
          item-list-id="home_recently_added"
          item-list-name="Ostatnio dodane"
          :index="index"
        />
      </div>
    </section>

    <!-- ── O nas ── -->
    <section v-if="brand.id === 'lavmag'" class="about-section container">
      <div class="about-section__inner">
        <div class="about-section__text">
          <h2 class="about-section__title">{{ content.about.title }}</h2>
          <p class="about-section__body">{{ content.about.body }}</p>
        </div>
        <div class="about-section__media">
          <NuxtImg
            :src="content.about.image"
            :alt="content.about.title"
            format="webp"
            loading="lazy"
            class="about-section__img"
            style="height: 100%; width: 100%"
          />
        </div>
      </div>
    </section>

    <!-- ── Jak to działa (tylko katalog) ──
         Klient nie kupuje sam, więc musi wiedzieć, co się stanie po wysłaniu
         zapytania i kiedy dostanie odpowiedź. -->
    <CatalogHowItWorks v-if="!sellingEnabled" />

    <!-- ── Find your style ──
         Sekcja żyje wyłącznie z kafli podanych w treści. Bez tego warunku sklep
         bez kafli (katalog) pokazywał nagłówek „Znajdź swój styl" nad pustą
         siatką — a sam nagłówek jest z języka mody, nie gadżetów reklamowych. -->
    <section v-if="content.styleCards.length" class="style-section container">
      <h2 class="section-title">Znajdź swój styl</h2>
      <!-- Liczba rzędów z liczby kafli (3 na rząd) — tylko w układzie
           trzykolumnowym, patrz media 1200 px. Sztywne dwa rzędy zostawiały
           pusty pas 380 px pod sklepem, który ma mniej niż sześć kafli. -->
      <div
        class="style-grid"
        :style="{ '--style-grid-rows': Math.ceil(content.styleCards.length / 3) }"
      >
        <NuxtLink
          v-for="card in content.styleCards"
          :key="card.href"
          :to="card.href"
          class="style-card"
        >
          <NuxtImg
            :src="card.image"
            :alt="card.label"
            format="webp"
            loading="lazy"
            class="style-card__img"
          />
          <div class="style-card__overlay" />
          <div class="style-card__content">
            <p class="style-card__title">{{ card.label }}</p>
            <p class="style-card__sub">{{ card.sub }}</p>
          </div>
        </NuxtLink>
      </div>
    </section>

    <!-- ── FAQ ── -->
    <!-- hydrate-on-visible: SSR-owane (treść + SEO w HTML), ale hydracja JS dopiero
         gdy sekcja wejdzie w viewport. Lighthouse nie scrolluje → nie płaci za ich
         hydrację (TBT). To sekcje na samym dole — user i tak scrolluje zanim wejdzie
         w interakcję (akordeon FAQ / formularz newslettera). -->
    <LazyVisibleFaqSection class="home__faq" hydrate-on-visible />

    <!-- ── Klamra marki (tylko katalog) ──
         Klientka poprosiła (16.09.2026), żeby na dole strony stanęła nazwa
         z claimem i tekst „Budujemy widoczność…". Tytułowa otwiera się dziś
         hasłem oferty („GADŻETY REKLAMOWE Z LOGO"), więc przekaz marki wraca
         tutaj i domyka stronę — klamra zamiast powtórzenia w jednym ekranie. -->
    <section v-if="!sellingEnabled" class="marka-klamra">
      <div class="marka-klamra__inner container">
        <p class="marka-klamra__nazwa">{{ brand.name }}</p>
        <p class="marka-klamra__claim">{{ content.hero.eyebrow }}</p>
        <p class="marka-klamra__tekst">{{ content.hero.text }}</p>
      </div>
    </section>

    <!-- ── Newsletter ── -->
    <LazyVisibleNewsletterSection class="home__newsletter" hydrate-on-visible />
  </div>
</template>

<style scoped>
.home {
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow-x: clip;
}

/* ── CWV: odroczenie renderu sekcji poniżej pierwszego ekranu ──────────────
 * content-visibility: auto — przeglądarka pomija layout/paint sekcji, dopóki nie
 * wejdzie w viewport. Lighthouse/boty nie scrollują, więc nie płacą za rendering
 * i style/layout treści poniżej ekranu (mniejszy TBT, szybszy FCP/LCP). Hero jest
 * wysokie (765px), więc wszystkie te sekcje są realnie poniżej pierwszego ekranu.
 * contain-intrinsic-size: auto <fallback> — przeglądarka zapamiętuje realną wysokość
 * po pierwszym wyrenderowaniu; rezerwacja miejsca = brak CLS (a ewentualna korekta
 * zachodzi przy scrollu = hadRecentInput, nie liczy się do CLS). NIE stosujemy na
 * hero/shipping-strip/sale-banner/bestsellerach (nad ekranem lub animacje reveal). */
.products-section,
.about-section,
.style-section,
.home__faq,
.home__newsletter {
  content-visibility: auto;
  contain-intrinsic-size: auto 600px;
}

/* ── Hero ── */
.hero {
  position: relative;
  height: 765px;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 100px;
}

[data-brand="youlook"] .hero {
  align-items: center;
  justify-content: start;
  padding-bottom: 0;
  padding-left: 100px;
}

/* Hero bez zdjęcia: sklep, który nie dostał jeszcze materiałów graficznych.
   Stała wysokość 765px liczona pod fotografię zostawiłaby ekran pustki
   z tekstem przyklejonym do dołu, więc tutaj hero jest blokiem tekstowym
   wyśrodkowanym na tle motywu marki. */
.hero--no-media {
  height: auto;
  min-height: 340px;
  align-items: center;
  padding: 4rem 1.5rem;
  background: var(--color-surface-alt, #f4f4f4);
}

.hero--no-media .hero__content {
  max-width: 720px;
  text-align: center;
}

/* Tekst hero jest domyślnie BIAŁY, bo normalnie leży na fotografii z ciemną
   nakładką. Bez zdjęcia tło jest jasne i biały napis znika — tytuł trzeba
   odwrócić na ciemny. Dotyczy każdej marki, która zostanie bez obrazu hero,
   nie tylko katalogu. */
.hero--no-media .hero__title {
  color: var(--color-black, #1f2933);
}

/* Nie `--color-muted`: na jasnym turkusie wychodzi 4.38:1, czyli tuż pod
   progiem AA dla tekstu tej wielkości. Przyciemnione do ~6:1. */
.hero--no-media .hero__eyebrow,
.hero--no-media .hero__subtitle {
  color: color-mix(in srgb, var(--color-black, #1f2933) 78%, #fff);
  /* `.hero__subtitle` ma `line-height: 0.8`, czyli MNIEJ niż wysokość pisma —
     przy jednym wierszu (tak jest u lavmaga) nie widać tego wcale, ale tekst
     katalogu łamie się na dwa i wiersze nachodzą na siebie. Nadpisujemy tylko
     dla wariantu bez zdjęcia, żeby nie ruszać hero sklepów sprzedażowych. */
  line-height: 1.6;
}

/* Wariant ze znakiem firmowym: tytułowa sklepu katalogowego, zbudowana jak
   post „Twoja marka. Nasz koncept." z Instagrama klientki (12.10.2026):
   PEŁNY turkus, białe logo, napisy w ciemnym tealu, biała kreska pod hasłem
   i duże miękkie łuki w tle.

   Wcześniej tło było rozbielonym turkusem, bo czarny logotyp i grafitowy
   tytuł traciły kontrast na pełnym. Ciemny teal marki (`--color-brand-deep`)
   ma na turkusie 4.8:1, więc pełna barwa wróciła bez utraty czytelności. */
.hero--marka {
  min-height: 460px;
  background: var(--color-accent, #64bfaa);
}

/* Łuki: dwa wycinki koła w jaśniejszym miętowym — ten sam motyw co na
   grafikach. Dekoracja, więc pod treścią (`.hero__content` ma z-index: 1)
   i bez reakcji na kursor. */
.hero--marka::before,
.hero--marka::after {
  content: "";
  position: absolute;
  aspect-ratio: 1;
  border-radius: 50%;
  background: var(--color-accent-light, #7cd4be);
  pointer-events: none;
}

.hero--marka::before {
  width: min(920px, 120vw);
  top: -55%;
  right: max(-300px, -25vw);
  opacity: 0.55;
}

.hero--marka::after {
  width: min(560px, 80vw);
  bottom: -70%;
  left: max(-220px, -30vw);
  opacity: 0.35;
}

/* Znak renderuje się tylko w sklepie katalogowym bez zdjęcia w hero — dziś
   wyłącznie proles. Szerokość to 80% dawnej clamp(150px, 26vw, 220px):
   30.10.2026 plik logo przycięto do rysunku (wcześniej rysunek zajmował 80%
   szerokości viewBox), a znak miał zostać tej samej wielkości. Marginesy
   odtwarzają przezroczysty pas pliku (~11,6% szerokości nad rysunkiem, ~12,8%
   pod nim), żeby blok hero nie zrobił się niższy. Atrybuty <img> (320×99)
   to proporcja przyciętego pliku. */
.hero__logo {
  width: clamp(120px, 20.8vw, 176px);
  height: auto;
  margin-top: clamp(14px, 2.41vw, 20px);
  /* Znak i hasło mają czytać się jako jeden blok, stąd odstęp mniejszy niż
     domyślny `gap` hero (dawne -4px liczone od dołu marginesu pliku). */
  margin-bottom: calc(clamp(15px, 2.66vw, 22px) - 4px);
}

/* Całe logo białe, jak na grafikach. Wersja „odwrócona" nie pasuje: ma biały
   napis, ale turkusowy liść, który na turkusowym tle by zniknął. */
.hero--marka .hero__logo {
  filter: brightness(0) invert(1);
}

/* Hasło oferty jest tu informacją („co sprzedajemy"), nie nazwą marki —
   nazwę niesie logotyp tuż nad nim. Dlatego tytuł schodzi o stopień
   w hierarchii: mniejszy niż domyślny hero, ale nadal pierwszy głos. */
.hero--marka .hero__title {
  font-size: clamp(1.75rem, 4.2vw, 3rem);
  letter-spacing: 0.02em;
  color: var(--color-brand-deep, #104646);
  /* Bazowe `line-height: 0.8` sklejało wiersze, a hasło łamało się na
     „GADŻETY REKLAMOWE Z / LOGO" z samotnym słowem w drugim wierszu.
     `balance` dzieli je na równe wiersze. */
  line-height: 1.1;
  text-wrap: balance;
}

/* Biała kreska pod hasłem — jak pod nagłówkami na grafikach marki. */
.hero--marka .hero__title::after {
  content: "";
  display: block;
  width: 56px;
  height: 3px;
  margin: 18px auto 0;
  border-radius: 2px;
  background: #fff;
}

/* Na turkusie biały drobny tekst miałby 2.19:1 — stąd ciemny teal (4.8:1). */
.hero--marka .hero__eyebrow,
.hero--marka .hero__subtitle {
  color: var(--color-brand-deep, #104646);
}

/* CTA musi być najmocniejszym punktem tytułowej — to jedyna konwersja tego
   sklepu. Do 03.11.2026 był to ciemny teal z białym napisem; klientka
   poprosiła o jaśniejsze przyciski zapytania („jak »zobacz produkt«").
   Turkus przycisku głównego zniknąłby na turkusowym tle hero, więc tu
   jaśniejszy wariant to biel z napisem w ciemnym tealu (10.6:1) — pigułka
   odcina się od tła tak jak biała kreska pod hasłem. Nie `btn-primary`:
   ten jest turkusowy. */
.hero--marka .hero__cta {
  background: #fff;
  border-color: #fff;
  color: var(--color-brand-deep, #104646);
  font-weight: var(--font-weight-bold, 700);
  letter-spacing: 0.08em;
  box-shadow: 0 6px 18px color-mix(in srgb, var(--color-brand-deep, #104646) 18%, transparent);
}

@media (hover: hover) {
  .hero--marka .hero__cta:hover {
    background: var(--color-brand-deep, #104646);
    border-color: var(--color-brand-deep, #104646);
    color: #fff;
  }
}

/* Klamra marki na dole strony. Pełny turkus: to ostatni akcent przed
   ciemnotealową stopką, więc ma odcinać, a nie zlewać się z sekcjami wyżej.

   Tekst w ciemnym tealu marki, nie biały: biały na turkusie ma 2.19:1, teal
   4.8:1 — i tak właśnie piszą grafiki klientki na turkusowym tle. */
.marka-klamra {
  position: relative;
  overflow: hidden;
  margin-top: 4rem;
  padding: 4.5rem 1.5rem;
  background: var(--color-accent, #64bfaa);
  color: var(--color-brand-deep, var(--color-black, #1f2933));
  text-align: center;
}

/* Łuk z grafik marki — ten sam motyw co w hero, żeby strona miała klamrę
   także wizualną. Pod treścią i bez reakcji na kursor. */
.marka-klamra::before {
  content: "";
  position: absolute;
  width: min(760px, 110vw);
  aspect-ratio: 1;
  top: -60%;
  left: max(-260px, -30vw);
  border-radius: 50%;
  background: var(--color-accent-light, #7cd4be);
  opacity: 0.45;
  pointer-events: none;
}

.marka-klamra__inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  max-width: 720px;
}

.marka-klamra__nazwa {
  margin: 0;
  font-size: clamp(1.5rem, 3.4vw, 2.25rem);
  font-weight: var(--font-weight-bold, 700);
  letter-spacing: 0.01em;
}

.marka-klamra__claim {
  margin: 0;
  font-size: clamp(1rem, 2vw, 1.25rem);
  letter-spacing: 0.06em;
  /* Bez wyciszenia przez `opacity`: przy grafitowym tekście (6.75:1) był na
     to zapas, przy tealu (4.8:1) już nie — 0.92 zepchnęłoby kontrast pod
     próg AA. Hierarchię niesie rozmiar. */
}

.marka-klamra__tekst {
  margin: 0.5rem 0 0;
  font-size: 0.9375rem;
  line-height: 1.75;
}

.hero__media {
  position: absolute;
  inset: 0;
}

.hero__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero__overlay {
  position: absolute;
  inset: 0;
  background: rgba(21, 21, 21, 0.09);
}

/* DISCANTO (key visual): tekst na zdjęciu w dolnym pasie kadru, na delikatnej
   nakładce w Obsidian Noir. Jasnoszare tło zdjęcia hero dawało białemu
   podpisowi (16 px) ok. 4:1, z nakładką ok. 6,5:1; góra kadru zostaje czysta. */
[data-brand="discanto"] .hero__overlay {
  background: linear-gradient(
    to top,
    rgba(22, 22, 22, 0.55) 0%,
    rgba(22, 22, 22, 0.3) 35%,
    rgba(22, 22, 22, 0) 65%
  );
}

.hero__content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
}

[data-brand="youlook"] .hero__content {
  align-items: flex-start;
}

/* Tagline nad tytułem — mniejszy i rozstrzelony, żeby czytał się jak podpis
   marki, a nie jak drugi nagłówek konkurujący z hasłem głównym. */
.hero__eyebrow {
  margin: 0 0 12px;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-normal);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--color-white);
  opacity: 0.85;
}

.hero__title {
  margin: 0;
  font-size: 2.5rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-white);
  line-height: 0.8;
}

[data-brand="youlook"] .hero__title {
  letter-spacing: 30%;
  font-weight: var(--font-weight-light);
}

.hero__subtitle {
  margin: 0;
  font-size: 1rem;
  font-weight: var(--font-weight-light);
  color: var(--color-white);
  line-height: 0.8;
}

.hero__cta {
  min-height: 48px;
  font-size: 0.875rem;
}

/* ── Shipping strip ── */
.shipping-strip {
  background: var(--color-black);
  min-height: 32px;
  overflow-x: clip;
}

.shipping-strip__track {
  display: flex;
  animation: marquee 20s linear infinite;
}

.shipping-strip__inner {
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 40px;
  padding: 8px 40px;
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-white);
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 100vw;
}

.shipping-strip__inner strong {
  font-weight: var(--font-weight-bold);
}

.shipping-strip__dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

@keyframes marquee {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}

/* ── Products section ── */
.products-section {
  padding: 40px 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.products-section--first {
  padding-top: 100px;
}

/* ── Section header ── */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.section-title {
  margin: 0;
  font-size: 1.5rem;
  /* Tokeny marki — patrz `.section-title` w main.css. */
  font-weight: var(--section-title-weight, var(--font-weight-light));
  color: var(--section-title-color, var(--color-black));
  text-transform: var(--section-title-transform, none);
  letter-spacing: var(--section-title-tracking, normal);
  line-height: 1;
}

/* ── Carousel ── */
.carousel-outer {
  position: relative;
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 44px;
  height: 44px;
  background: var(--color-white);
  border: 1px solid var(--color-border);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-black);
  transition: background var(--transition-fast);
  border-radius: 0;
}

.carousel-arrow:hover {
  background: var(--color-bg);
}

.carousel-arrow--prev {
  left: 0;
}

.carousel-arrow--next {
  right: 0;
}

/* ── Tabs ── */
.tabs {
  display: flex;
  gap: 40px;
  align-items: center;
  overflow-x: auto;
  scrollbar-width: none;
}

.tabs::-webkit-scrollbar {
  display: none;
}

.tabs__btn {
  background: none;
  border: none;
  border-bottom: 1px solid transparent;
  padding: 0 0 4px;
  font-family: var(--font-body);
  font-size: 1.5rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  cursor: pointer;
  transition: color var(--transition-fast);
  line-height: 1;
  white-space: nowrap;
  flex-shrink: 0;
}

.tabs__btn--active {
  color: var(--color-black);
  border-bottom-color: var(--color-black);
}

.tabs__btn:hover:not(.tabs__btn--active) {
  color: var(--color-black);
}

/* ── Recently added (4×3 grid) ── */
.recently-added-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px;
  overflow: hidden;
}

@media (max-width: 768px) {
  .recently-added-grid :deep(.product-card__info) {
    padding: 8px;
    gap: 4px;
  }

  .recently-added-grid :deep(.product-card__name) {
    font-size: 0.8125rem;
  }

  .recently-added-grid :deep(.product-card__price) {
    font-size: 0.8125rem;
  }

  .recently-added-grid :deep(.product-card__sizes) {
    font-size: 0.6875rem;
  }

  .recently-added-grid :deep(.product-card__info-cart-btn) {
    height: 34px;
    font-size: 0.75rem;
    letter-spacing: 0.02em;
  }
}

/* ── Sale banner (tekst i przycisk naniesione na obrazek) ── */
.sale-banner {
  position: relative;
  height: 760px;
  overflow: hidden;
  margin: 40px 0;
}

.sale-banner__link {
  display: block;
  position: absolute;
  inset: 0;
}

.sale-banner__media {
  position: absolute;
  inset: 0;
}

.sale-banner__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sale-banner__img--mobile {
  display: none;
}

/* Nakładka tekstowa banera — desktop: blok po prawej, wyśrodkowany w pionie. */
.sale-banner__content {
  position: absolute;
  z-index: 1;
  top: 50%;
  right: 9%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 22px;
  text-align: center;
}

[data-brand="youlook"] .sale-banner__content {
  right: unset;
  left: 5%;
}

.sale-banner__title {
  margin: 0;
  font-size: 6rem;
  font-weight: var(--font-weight-light);
  letter-spacing: 0.5rem;
  color: var(--color-white);
  line-height: 1;
  text-shadow: 0 1px 14px rgba(21, 21, 21, 0.18);
}

[data-brand="youlook"] .sale-banner__title {
  font-size: 4rem;
  letter-spacing: 30%;
}

.sale-banner__subtitle {
  margin: 0;
  font-size: 2rem;
  font-weight: var(--font-weight-light);
  color: var(--color-white);
  text-shadow: 0 1px 12px rgba(21, 21, 21, 0.18);
}

[data-brand="youlook"] .sale-banner__subtitle {
  font-size: 1rem;
}

.sale-banner__cta {
  min-height: 48px;
  margin-top: 6px;
  padding: 20px 30px;
  font-size: 1.5rem;
  letter-spacing: 0.1rem;
}

[data-brand="youlook"] .sale-banner__cta {
  font-size: 1rem;
}

/* ── About section ── */
.about-section {
  padding: 80px 0;
}

.about-section__text {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
}

.about-section__inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: stretch;
}

.about-section__title {
  margin: 0 0 32px;
  font-size: 126px;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 0.85;
  letter-spacing: -0.02em;
}

.about-section__body {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 1.4;
}

.about-section__media {
  overflow: hidden;
  height: 100%;
}

.about-section__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
}

/* ── Style section ── */
.style-section {
  padding: 40px 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.style-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(var(--style-grid-rows, 2), 380px);
  gap: 4px;
}

.style-card {
  position: relative;
  display: flex;
  align-items: flex-end;
  padding: 50px;
  overflow: hidden;
  background: #2a2a2a;
}

[data-brand="youlook"] .style-card {
  border-radius: 15px;
}

.style-card__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.style-card__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.5) 0%, transparent 60%);
}

.style-card__content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.style-card__title {
  margin: 0;
  /* Nazwa kafla gra rolę nagłówka (to <p>, więc reguła h1–h6 go nie łapie). */
  font-family: var(--font-heading);
  font-size: 3rem;
  font-weight: var(--font-weight-light);
  color: var(--color-white);
  line-height: 1;
}

.style-card__sub {
  margin: 0;
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-white);
  line-height: 1;
}

.home__faq {
  margin-top: 40px;
}

.home__newsletter {
  margin-top: 40px;
}

/* ── Responsive ── */
@media (max-width: 1200px) {
  .recently-added-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  /* Przy dwóch kolumnach liczba rzędów z --style-grid-rows (liczona na trzy
     kolumny) byłaby za mała: trzy kafle discanto dawały jeden rząd i trzeci kafel
     w niejawnym rzędzie `auto`. Stałe dwa rzędy to zachowanie sprzed tej
     zmiennej — lavmag/youlook bez zmian, discanto 3 kafle = 2 pełne rzędy. */
  .style-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: repeat(2, 380px);
  }
}

@media (max-width: 960px) {
  .about-section__inner {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .about-section__title {
    font-size: 72px;
  }

  .about-section {
    padding: 20px 0;
  }

  .hero {
    height: 480px;
    padding-bottom: 80px;
  }

  [data-brand="youlook"] .hero {
    align-items: center;
    justify-content: center;
    padding-bottom: 0;
    padding-left: 0;
  }

  [data-brand="youlook"] .hero__content {
    align-items: center;
  }

  .hero__title {
    font-size: 0.875rem;
    font-weight: var(--font-weight-light);
  }

  .hero__subtitle {
    font-size: 1.5rem;
    letter-spacing: 0.4rem;
  }

  /* Hero bez zdjęcia (wariant katalogowy) ma wysokość z treści. Sztywne
     `height: 480px` wyżej leży w pliku później niż `.hero--no-media
     { height: auto }`, więc wygrywało — a logo + nadpis + hasło + opis +
     przycisk nie mieszczą się w 480 px i `overflow: hidden` obcinał logo
     u góry i przycisk na dole. Podwójna klasa przebija regułę `.hero`. */
  .hero.hero--no-media {
    height: auto;
    padding: 48px 20px 56px;
  }

  /* Reguły wyżej odwracają role pod lavmaga: tytuł mały i cienki, podtytuł
     duży i rozstrzelony — bo tam podtytułem jest krótkie hasło „MODA LESS
     WASTE". W katalogu tytułem jest hasło oferty, a podtytułem całe zdanie
     opisu; rozstrzelone o 0.4rem rozsypywało się litera po literze. */
  .hero--marka .hero__title {
    font-size: clamp(1.625rem, 7vw, 2.25rem);
    font-weight: var(--font-weight-bold, 700);
  }

  .hero--marka .hero__subtitle {
    font-size: 0.9375rem;
    letter-spacing: normal;
  }

  /* Claim DISCANTO (28 znaków) łamie się tu na dwa wiersze, a line-height 0.8
     sklejał je ze sobą. balance wyrównuje długość wierszy. */
  [data-brand="discanto"] .hero__subtitle {
    line-height: 1.2;
    text-align: center;
    text-wrap: balance;
  }

  .products-section--first {
    padding-top: 60px;
  }

  .recently-added-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .sale-banner {
    /* Wysokość z proporcji obrazka mobilnego (403×325), by cover nie przyciął
       pełnej fotografii pod nakładką tekstu/przycisku. */
    height: auto;
    aspect-ratio: 403 / 325;
  }

  .sale-banner__img--desktop {
    display: none;
  }

  .sale-banner__img--mobile {
    display: block;
  }

  /* Nakładka banera — mobile: wyśrodkowana w poziomie, lekko poniżej środka
     (top 58%), by napis nie zachodził na twarz modelki w kadrze 403×325. */
  .sale-banner__content {
    top: 58%;
    right: auto;
    left: 50%;
    transform: translate(-50%, -50%);
    gap: 12px;
    width: 100%;
    padding: 0 24px;
    align-items: center;
  }

  .sale-banner__title {
    font-size: 6rem;
    letter-spacing: 0.35rem;
  }

  .sale-banner__subtitle {
    font-size: 1.5rem;
    letter-spacing: 0.15rem;
  }

  .sale-banner__cta {
    min-height: 40px;
    padding: 20px 30px;
    font-size: 1.5rem;
  }

  .style-grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
    grid-template-rows: none;
    grid-auto-rows: 120px;
  }

  .style-card {
    padding: 24px;
  }

  .style-card__title {
    font-size: 2rem;
  }

  .home__newsletter {
    margin-top: 20px;
  }
}

@media (max-width: 640px) {
  .about-section__title {
    font-size: 48px;
  }

  .recently-added-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .recently-added-grid > *:nth-child(n + 5) {
    display: none;
  }

  .products-section {
    padding: 20px 0;
  }

  .style-grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
    grid-template-rows: none;
    grid-auto-rows: 120px;
  }

  .sale-banner__title {
    font-size: 3rem;
  }

  .sale-banner__subtitle {
    font-size: 1rem;
  }

  .sale-banner__cta {
    font-size: 1rem;
    padding: 16px 30px;
  }

  [data-brand="youlook"] .hero__title {
    font-size: 1.5rem;
    font-weight: var(--font-weight-light);
  }
}
</style>
